import React, { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import { GridLegacy as Grid } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAllStores } from '../../Query/ManageStoreQuery';
import {
  RejectedOrdersReportRequest,
  RejectedOrdersReportRequestRejectedByEnum,
  RejectedOrdersReportRequestDateRangeEnum,
} from '../../../client-sdk/api';
import { useDownloadRejectedOrdersReport } from '../../Query/ReportsQuery';

const RejectedOrdersReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { mutate: downloadReport, isPending } = useDownloadRejectedOrdersReport();

  const [reportType] = useState<'daily' | 'weekly' | 'monthly' | 'custom'>('custom');
  const [dateFrom, setDateFrom] = useState<Dayjs | null>(dayjs().startOf('day'));
  const [dateTo, setDateTo] = useState<Dayjs | null>(dayjs().endOf('day'));
  const [selectedStore, setSelectedStore] = useState<string>('');
  const [rejectedBy, setRejectedBy] = useState<string>('');

  const storeQueryParams = useMemo(
    () => ({
      sortBy: 'createdAt',
      sortOrder: 'asc' as const,
      page: 1,
      limit: 100,
      addedByFilter: 'All',
      statusFilter: 'All',
      search: undefined,
    }),
    []
  );

  const { data: storesData } = useAllStores(storeQueryParams);

  const storeOptions =
    (storesData as any)?.data?.map((s: any) => ({
      value: s.id,
      label: s.nameEnglish || s.restaurantDetails?.storeName || '',
    })) ?? [];

  // Sync dates with report type
  useEffect(() => {
    const today = dayjs();
    switch (reportType) {
      case 'daily':
        setDateFrom(today.startOf('day'));
        setDateTo(today.endOf('day'));
        break;
      case 'weekly': {
        const start = today.startOf('day');
        const todayEnd = today.endOf('day');
        const tentativeEnd = start.add(6, 'day').endOf('day');
        const end = tentativeEnd.isAfter(todayEnd) ? todayEnd : tentativeEnd;
        setDateFrom(start);
        setDateTo(end);
        break;
      }
      case 'monthly':
        setDateFrom(today.startOf('month'));
        setDateTo(today.endOf('month'));
        break;
      case 'custom':
        break;
    }
  }, [reportType]);

  // Weekly: to = from + 6 days (capped at today)
  useEffect(() => {
    if (reportType === 'weekly' && dateFrom) {
      const start = dateFrom.startOf('day');
      const todayEnd = dayjs().endOf('day');
      const tentativeEnd = start.add(6, 'day').endOf('day');
      const end = tentativeEnd.isAfter(todayEnd) ? todayEnd : tentativeEnd;
      setDateTo(end);
    }
  }, [reportType, dateFrom]);

  // Daily: keep to in sync with chosen day
  useEffect(() => {
    if (reportType === 'daily' && dateFrom) {
      setDateTo(dateFrom.endOf('day'));
    }
  }, [reportType, dateFrom]);

  const handleDownload = () => {
    const fromDay = dateFrom ?? dayjs();
    const toDay = dateTo ?? dateFrom ?? dayjs();

    let dateRange: RejectedOrdersReportRequestDateRangeEnum;
    let from: string | undefined;
    let to: string | undefined;

    if (reportType === 'daily') {
      const target = fromDay;
      dateRange = RejectedOrdersReportRequestDateRangeEnum.Today;
      from = target.startOf('day').toISOString();
      to = target.endOf('day').toISOString();
    } else if (reportType === 'weekly') {
      const start = fromDay.startOf('day');
      const todayEnd = dayjs().endOf('day');
      const tentativeEnd = start.add(6, 'day').endOf('day');
      const end = tentativeEnd.isAfter(todayEnd) ? todayEnd : tentativeEnd;
      dateRange = RejectedOrdersReportRequestDateRangeEnum.Custom;
      from = start.toISOString();
      to = end.toISOString();
    } else if (reportType === 'monthly') {
      const base = fromDay;
      const start = base.startOf('month');
      const end = base.endOf('month');
      dateRange = RejectedOrdersReportRequestDateRangeEnum.ThisMonth;
      from = start.toISOString();
      to = end.toISOString();
    } else {
      dateRange = RejectedOrdersReportRequestDateRangeEnum.Custom;
      from = fromDay.startOf('day').toISOString();
      to = toDay.endOf('day').toISOString();
    }

    let rejectedByEnum: RejectedOrdersReportRequestRejectedByEnum | undefined;
    if (rejectedBy === 'merchant') {
      rejectedByEnum = RejectedOrdersReportRequestRejectedByEnum.MerchantRejected;
    } else if (rejectedBy === 'driver') {
      rejectedByEnum = RejectedOrdersReportRequestRejectedByEnum.DriverRejected;
    }

    const request: RejectedOrdersReportRequest = {
      dateRange,
      dateFrom: from,
      dateTo: to,
      storeIds: selectedStore ? [selectedStore] : undefined,
      rejectedBy: rejectedByEnum,
    };

    downloadReport(request);
  };

  // Common input styling
  const commonInputSx = {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: '40px !important',
      minHeight: '40px !important',
      '& .MuiSelect-select': {
        height: '40px',
        display: 'flex',
        alignItems: 'center',
        paddingTop: 0,
        paddingBottom: 0,
        lineHeight: '40px',
      },
      '& .MuiAutocomplete-input': {
        padding: '0 14px !important',
        height: '40px !important',
        minHeight: '40px !important',
        boxSizing: 'border-box',
      },
      '& .MuiInputBase-input': {
        padding: '0 14px !important',
        height: '40px !important',
        minHeight: '40px !important',
        boxSizing: 'border-box',
      },
      '& input': {
        padding: '0 14px !important',
        height: '40px !important',
        minHeight: '40px !important',
        boxSizing: 'border-box',
      },
    },
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ 
        py: 3,
        px: { xs: 2, sm: 3 }
      }}>
        <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
          <Typography 
            variant="h4" 
            fontWeight="bold" 
            mb={3} 
            sx={{ 
              color: 'text.primary',
              fontSize: { xs: '1.5rem', md: '2rem' }
            }}
          >
            {t('nav.rejectedOrdersReport')}
          </Typography>

          <Paper 
            elevation={1}
            sx={{ 
              p: { xs: 2, sm: 3 }, 
              mb: 3,
            }}
          >
            <Grid container spacing={3}>
              {/* Date From */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {t('reports.dateFrom')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    {/* <DatePicker
                      value={dateFrom}
                      onChange={(v) => {
                        if (!v) {
                          setDateFrom(null);
                          if (reportType === 'monthly') {
                            setDateTo(null);
                          }
                          return;
                        }

                        if (reportType === 'monthly') {
                          const base = v.startOf('month');
                          setDateFrom(base);
                          setDateTo(base.endOf('month'));
                        } else {
                          setDateFrom(v);
                        }
                      }}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: commonInputSx,
                          placeholder: t('reports.dateFrom'),
                        },
                      }}
                    /> */}
                    <DatePicker
  value={dateFrom}
  onChange={(v) => {
    if (!v) {
      setDateFrom(null);
      if (reportType === 'monthly') {
        setDateTo(null);
      }
      return;
    }

    if (reportType === 'monthly') {
      const base = v.startOf('month');
      setDateFrom(base);
      setDateTo(base.endOf('month'));
    } else {
      setDateFrom(v);
    }
  }}
  maxDate={dayjs()}
  slotProps={{
    textField: {
      fullWidth: true,
      sx: {
        ...commonInputSx,
        '& .MuiInputBase-input': {
          marginLeft: i18n.language === 'ar' ? '82px' : '0px', // Add spacing in Arabic
        },
      },
      placeholder: t('reports.dateFrom'),
    },
  }}
/>
                  </Grid>
                </Grid>
              </Grid>

              {/* Date To */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {t('reports.dateTo')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <DatePicker
                      value={dateTo}
                      onChange={(v) => setDateTo(v)}
                      disabled={reportType === 'daily' || reportType === 'monthly'}
                      maxDate={dayjs()}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: commonInputSx,
                          placeholder: t('reports.dateTo'),
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Stores */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {t('reports.stores')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <FormControl fullWidth>
                      <Select
                        value={selectedStore}
                        onChange={(e) => setSelectedStore(e.target.value)}
                        sx={commonInputSx}
                      >
                        <MenuItem value="">{t('reports.all')}</MenuItem>
                        {storeOptions.map((s: { value: string; label: string }) => (
                          <MenuItem key={s.value} value={s.value}>
                            {s.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Rejected By */}
              <Grid item xs={12}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={3}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {t("common.rejectedBy")}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={9}>
                    <FormControl fullWidth>
                      <Select
                        value={rejectedBy}
                        onChange={(e) => setRejectedBy(e.target.value)}
                        sx={commonInputSx}
                      >
                        <MenuItem value="">{t('reports.all')}</MenuItem>
                        <MenuItem value="merchant">{t("reports.store")}</MenuItem>
                        <MenuItem value="driver">{t("reports.driver")}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Grid>

              {/* Download Button */}
              <Grid item xs={12}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  mt: 2,
                  pt: 2,
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Button
                    variant="contained"
                    onClick={handleDownload}
                    disabled={isPending}
                    size="large"
                    sx={{ 
                      minWidth: 200,
                      height: '40px',
                    }}
                  >
                    {isPending ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1.5, color: 'white' }} />
                        {t('reports.downloading')}
                      </>
                    ) : (
                      t('reports.download')
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default RejectedOrdersReport;


